import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Shared helpers
const hasOnlySpaces = (value: string): boolean => value.trim().length === 0;

const makeNoAllSpacesAsRequiredError = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined) return null;
    const str = String(raw);
    if (str.length > 0 && hasOnlySpaces(str)) return { required: true };
    return null;
  };
};

const makeTrimError = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const str = String(raw);
    return str.trim() !== str ? { trim: true } : null;
  };
};

const makeMinLength = (min: number, errorKey: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const trimmed = String(raw).trim();
    if (trimmed.length > 0 && trimmed.length < min) {
      return {
        [errorKey]: { requiredLength: min, actualLength: trimmed.length }
      };
    }
    return null;
  };
};

const makeMaxLength = (max: number, errorKey: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const trimmed = String(raw).trim();
    if (trimmed.length > max) {
      return {
        [errorKey]: { requiredLength: max, actualLength: trimmed.length }
      };
    }
    return null;
  };
};

const makeAllowedChars = (re: RegExp, errorKey: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;
    const str = String(raw);
    if (!re.test(str)) return { [errorKey]: true };
    return null;
  };
};

// Benefits: 10-1000, alphabets, numbers, spaces, common punctuation (., ,, :, ;, !, ?, (, ), -, /, &)
// IMPORTANT: Do not allow only spaces.
const benefitsAllowedCharsRe = /^[A-Za-z0-9 .,:;!?()\-\/&]+$/;

export const benefitsValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || String(raw) === '') return null;

    const str = String(raw);
    if (hasOnlySpaces(str)) return { required: true };

    if (!benefitsAllowedCharsRe.test(str)) return { benefitsAllowedChars: true };

    const trimmed = str.trim();
    if (trimmed.length < 10) {
      return { benefitsMinLength: { requiredLength: 10, actualLength: trimmed.length } };
    }
    if (trimmed.length > 1000) {
      return { benefitsMaxLength: { requiredLength: 1000, actualLength: trimmed.length } };
    }

    return null;
  };
};

// Useful For: required, 2-200, alphabets, numbers, spaces, comma, hyphen, ampersand
const usefulForAllowedCharsRe = /^[A-Za-z0-9 ,&-]+$/;

export const usefulForValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || String(raw) === '') return null;

    const str = String(raw);
    if (hasOnlySpaces(str)) return { required: true };

    if (!usefulForAllowedCharsRe.test(str)) return { usefulForAllowedChars: true };

    const trimmed = str.trim();
    if (trimmed.length < 2) {
      return { usefulForMinLength: { requiredLength: 2, actualLength: trimmed.length } };
    }
    if (trimmed.length > 200) {
      return { usefulForMaxLength: { requiredLength: 200, actualLength: trimmed.length } };
    }

    return null;
  };
};

// Features: required, 10-1000, common punctuation, do not allow only spaces
// We reuse the benefits-like allowed punctuation set (common punctuation requirement is vague).
const featuresAllowedCharsRe = benefitsAllowedCharsRe;

export const featuresValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || String(raw) === '') return null;

    const str = String(raw);
    if (hasOnlySpaces(str)) return { required: true };

    if (!featuresAllowedCharsRe.test(str)) return { featuresAllowedChars: true };

    const trimmed = str.trim();
    if (trimmed.length < 10) {
      return { featuresMinLength: { requiredLength: 10, actualLength: trimmed.length } };
    }
    if (trimmed.length > 1000) {
      return { featuresMaxLength: { requiredLength: 1000, actualLength: trimmed.length } };
    }

    return null;
  };
};

// Validity - Fixed Date: date picker only, past dates are not allowed.
export const fixedDateNotPastValidator = (todayDateIso: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (!raw) return null;
    const value = String(raw);

    // Expect ISO date from input[type=date] => yyyy-mm-dd
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return { invalidDate: true };
    if (value < todayDateIso) return { pastDate: true };
    return null;
  };
};

// Validity - No. of Days: digits only, >0, max 3650
export const daysValidityValidator = (maxDays: number = 3650): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;

    const str = String(raw);
    if (!/^\d+$/.test(str)) return { digitsOnly: true };

    const n = Number(str);
    if (!Number.isFinite(n) || n < 1) return { minValue: true };
    if (n > maxDays) return { maxValue: true };
    return null;
  };
};

const nonNegativeAmountRe = /^\d+(?:\.\d{1,2})?$/;

export const nonNegativeDigitsValidator = (options: { min?: number; max?: number; errorKey?: string } = {}): ValidatorFn => {
  const { min = 0, max, errorKey = 'nonNegativeDigits' } = options;

  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;

    const str = String(raw).trim();
    if (!nonNegativeAmountRe.test(str)) return { [errorKey]: true };

    const n = Number(str);
    if (!Number.isFinite(n)) return { [errorKey]: true };
    if (n < min) return { [errorKey]: true };
    if (typeof max === 'number' && n > max) return { [errorKey]: true };
    return null;
  };
};

// Maximum integer-digit constraint for amounts.
// Useful to cap values like price/discount to a maximum of 7 digits while allowing
// cents/paise decimals such as 1111.00.
export const maxDigitsLengthValidator = (maxDigits: number, errorKey: string): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;

    const str = String(raw).trim();
    if (!nonNegativeAmountRe.test(str)) return { [errorKey]: true };

    const digits = str.split('.')[0];
    if (digits.length > maxDigits) {
      return { [errorKey]: { requiredLength: maxDigits, actualLength: digits.length } };
    }

    return null;
  };
};


// How to Use: required, 10-3000, do not allow only spaces
const howToUseAllowedCharsRe = benefitsAllowedCharsRe;

export const howToUseValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || String(raw) === '') return null;

    const str = String(raw);
    if (hasOnlySpaces(str)) return { required: true };

    if (!howToUseAllowedCharsRe.test(str)) return { howToUseAllowedChars: true };

    const trimmed = str.trim();
    if (trimmed.length < 10) {
      return { howToUseMinLength: { requiredLength: 10, actualLength: trimmed.length } };
    }
    if (trimmed.length > 3000) {
      return { howToUseMaxLength: { requiredLength: 3000, actualLength: trimmed.length } };
    }

    return null;
  };
};

// Others: optional, max 1000; allow alphabets/numbers/spaces/common punctuation
const othersAllowedCharsRe = benefitsAllowedCharsRe;

export const othersOptionalValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') return null;

    const str = String(raw);
    if (!othersAllowedCharsRe.test(str)) return { othersAllowedChars: true };

    const trimmed = str.trim();
    if (trimmed.length > 1000) {
      return { othersMaxLength: { requiredLength: 1000, actualLength: trimmed.length } };
    }

    return null;
  };
};

// Terms & Conditions: required, 20-5000, do not allow only spaces
const termsAllowedCharsRe = benefitsAllowedCharsRe;

export const termsAndConditionValidator = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || String(raw) === '') return null;

    const str = String(raw);
    if (hasOnlySpaces(str)) return { required: true };

    if (!termsAllowedCharsRe.test(str)) return { termsAllowedChars: true };

    const trimmed = str.trim();
    if (trimmed.length < 20) {
      return { termsMinLength: { requiredLength: 20, actualLength: trimmed.length } };
    }
    if (trimmed.length > 5000) {
      return { termsMaxLength: { requiredLength: 5000, actualLength: trimmed.length } };
    }

    return null;
  };
};

